import type { LineSeriesOption } from 'echarts'
import { detectDataGaps, insertNullsAtGaps, createGapBridgeSeries } from './dataGapDetection'
import { resolveSeriesType } from './seriesTypes'
import { brandColors } from './useChartSeries'

export interface DataGapVisualizationOptions {
  highlightPowerOff: boolean
}

/**
 * Walks the built series array, detects time-series gaps in each eligible line
 * series, and:
 *   - inserts null points so ECharts breaks the line at the gap
 *   - appends a dashed bridge series spanning the gap
 *   - collects gap timestamps for the power-off band overlay (when enabled)
 *
 * Always appends a `__power_off_bands` series with a stable id (empty data
 * when power-off highlighting is disabled) so ECharts can update it in-place
 * with notMerge=false rather than accumulating stale copies.
 *
 * Returns a new array — the input is not mutated.
 */
export function applyDataGapVisualization(
  series: LineSeriesOption[],
  { highlightPowerOff }: DataGapVisualizationOptions,
): LineSeriesOption[] {
  const built = [...series]
  const allPowerOffGaps: { start: string; end: string }[] = []
  const seenPowerOffGapKeys = new Set<string>()

  for (let i = built.length - 1; i >= 0; i--) {
    const s = built[i]
    if (s.type !== 'line' || !s.data?.length || (s.data as unknown[]).length < 2) continue
    if (resolveSeriesType(typeof s.name === 'string' ? s.name : '').skipGapDetection) continue
    if (s.markArea && (s.data as unknown[]).every(d => Array.isArray(d) && (d as unknown[])[1] == null)) continue

    const sortedData = [...(s.data as [string, number][])].sort(
      (a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime()
    )
    const gaps = detectDataGaps(sortedData)
    if (gaps.length === 0) continue

    built[i] = {
      ...s,
      data: insertNullsAtGaps(sortedData, gaps) as LineSeriesOption['data'],
      connectNulls: false,
    }

    const lineColor = s.lineStyle?.color
    const itemColor = (s.itemStyle as { color?: string } | undefined)?.color
    const color =
      (typeof lineColor === 'string' ? lineColor : undefined) ??
      (typeof itemColor === 'string' ? itemColor : undefined) ??
      brandColors[0]

    const bridge = createGapBridgeSeries(typeof s.name === 'string' ? s.name : undefined, gaps, color)
    if (bridge) built.push(bridge)

    if (highlightPowerOff) {
      for (const g of gaps) {
        const key = `${g.startPoint[0]}-${g.endPoint[0]}`
        if (!seenPowerOffGapKeys.has(key)) {
          seenPowerOffGapKeys.add(key)
          allPowerOffGaps.push({ start: g.startPoint[0], end: g.endPoint[0] })
        }
      }
    }
  }

  built.push({
    type: 'line' as const,
    id: '__power_off_bands',
    data: [],
    lineStyle: {},
    markArea: {
      silent: true,
      itemStyle: { color: 'rgba(180, 180, 180, 0.25)' },
      label: { show: false },
      data: highlightPowerOff && allPowerOffGaps.length > 0
        ? allPowerOffGaps.map(g => [{ coord: [g.start] }, { coord: [g.end] }])
        : [],
    },
  } as LineSeriesOption)

  return built
}
